// Keeps the page a logged-out user actually asked for across the bounce through
// /app/login, so a deep link mailed out in a report (a filtered conversation list,
// say) still lands on the right page after signing in.
//
// The destination travels two ways on purpose:
//   - `?return_to=` on the login URL, the readable contract the help center portal
//     already emits (public/api/v1/portals/base_controller.rb).
//   - a sessionStorage mirror, because Google sign-in leaves the app entirely and
//     comes back via a login URL Rails builds from scratch, which cannot carry the
//     original query string.
const RETURN_TO_STORAGE_KEY = 'cw_login_return_to';
const LOGIN_PATH = '/app/login';

export const RETURN_TO_PARAM = 'return_to';

// A header splitting or path smuggling attempt would need one of these.
const hasControlCharacter = value =>
  Array.from(value).some(character => {
    const code = character.charCodeAt(0);
    return code < 0x20 || code === 0x7f;
  });

// Only a root relative path of our own origin may be used as a post login
// destination. Anything else would turn the login page into an open redirect.
export const sanitizeReturnTo = path => {
  if (typeof path !== 'string') return '';

  const value = path.trim();
  if (!value.startsWith('/')) return '';
  // "//host" and "/\host" are protocol relative URLs, not local paths.
  if (value[1] === '/' || value[1] === '\\') return '';
  if (hasControlCharacter(value)) return '';
  // Bouncing back to the login page itself would just loop.
  if (value.split('?')[0].replace(/\/+$/, '') === LOGIN_PATH) return '';

  return value;
};

export const getCurrentPathForRedirect = () => {
  const { pathname, search, hash } = window.location;
  return `${pathname}${search}${hash}`;
};

// sessionStorage throws in some privacy modes, and losing the redirect is never
// worth breaking the login flow over.
const readStash = () => {
  try {
    return window.sessionStorage.getItem(RETURN_TO_STORAGE_KEY) || '';
  } catch (error) {
    return '';
  }
};

const writeStash = value => {
  try {
    window.sessionStorage.setItem(RETURN_TO_STORAGE_KEY, value);
  } catch (error) {
    // Storage unavailable. The ?return_to= param still covers the common path.
  }
};

export const clearLoginRedirect = () => {
  try {
    window.sessionStorage.removeItem(RETURN_TO_STORAGE_KEY);
  } catch (error) {
    // Nothing to clear if storage is unavailable.
  }
};

// Builds the login URL to bounce an unauthenticated visitor to, remembering where
// they were headed.
export const getLoginURLWithReturnTo = (path = getCurrentPathForRedirect()) => {
  const value = sanitizeReturnTo(path);
  if (!value) return LOGIN_PATH;

  writeStash(value);
  return `${LOGIN_PATH}?${RETURN_TO_PARAM}=${encodeURIComponent(value)}`;
};

// Returns the remembered destination once, preferring the one on the URL, and
// forgets it so a later sign in cannot reuse a stale path.
export const consumeLoginRedirect = returnTo => {
  const stashed = readStash();
  clearLoginRedirect();
  return sanitizeReturnTo(returnTo) || sanitizeReturnTo(stashed);
};
